import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { X, Save, Upload, Link, Plus, Ruler } from 'lucide-react';
import { uploadWithFeedback } from '../../utils/cloudinaryUpload';
import { toast } from 'sonner';
import FocalPointPicker from '../../components/FocalPointPicker';

const MODEL_NAMES = {
  'Chain': ['Mangalsutra', 'Cocktail', 'Box Chain', 'Rope Chain', 'Cable Chain', 'Figaro', 'Curb Chain'],
  'Bangle': ['Traditional', 'Modern', 'Kada', 'Cuff'],
  'Bracelet': ['Tennis', 'Charm', 'Cuff', 'Link', 'Bangle Style'],
  'Ring': ['Solitaire', 'Eternity', 'Cocktail', 'Band', 'Halo', 'Engagement'],
  'Necklace': ['Choker', 'Princess', 'Matinee', 'Opera', 'Rope', 'Collar'],
  'Earring': ['Stud', 'Drop', 'Hoop', 'Jhumka', 'Chandbali', 'Danglers'],
  'Pendant': ['Solitaire', 'Heart', 'Cross', 'Religious', 'Initial'],
  'Anklet': ['Traditional', 'Modern', 'Charm'],
  'Nose Pin': ['Stud', 'Ring', 'Hoop'],
  'Mangalsutra': ['Traditional', 'Modern', 'Diamond', 'Short', 'Long']
};

const defaultLengthOptions = ['14 inch', '16 inch', '18 inch', '20 inch', '22 inch', '24 inch'];
const defaultColorOptions = ['Yellow Gold', 'Rose Gold', 'White Gold', 'Two-Tone'];

export default function ProductForm({
  productForm,
  setProductForm,
  editingProduct,
  onSubmit,
  onCancel,
  metalCaratOptions,
  modelCategoryOptions,
  modelNameOptions,
  otherMaterialOptions,
  cloudinaryEnabled
}) {
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const [newLengthOption, setNewLengthOption] = useState('');
  const [newColorOption, setNewColorOption] = useState('');
  const fileInputRefs = useRef([]);

  const handleImageUpload = async (index, file) => {
    if (!file) return;
    setUploadingIndex(index);
    try {
      const result = await uploadWithFeedback(file, 'products', toast);
      const newImages = [...productForm.images];
      newImages[index] = result.url;
      setProductForm({ ...productForm, images: newImages });
    } catch (error) {
    } finally {
      setUploadingIndex(null);
      if (fileInputRefs.current[index]) {
        fileInputRefs.current[index].value = '';
      }
    }
  };

  const addImageSlot = () => {
    const newImages = [...productForm.images, ''];
    const newFocals = [...(productForm.image_focal_points || []), { x: 50, y: 50 }];
    setProductForm({ ...productForm, images: newImages, image_focal_points: newFocals });
  };

  const removeImageSlot = (index) => {
    if (productForm.images.length <= 1) return;
    const newImages = productForm.images.filter((_, i) => i !== index);
    const newFocals = (productForm.image_focal_points || []).filter((_, i) => i !== index);
    setProductForm({ ...productForm, images: newImages, image_focal_points: newFocals });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white p-6 rounded-lg border border-gray-200 mb-6"
      data-testid="product-form"
    >
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-serif text-xl font-semibold text-[#1a3a5c]">
          {editingProduct ? 'Edit Product' : 'Add New Product'}
        </h3>
        <button onClick={onCancel} className="text-gray-500 hover:text-gray-700">
          <X className="h-6 w-6" />
        </button>
      </div>

      <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Basic Fields */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Product Name</label>
          <input type="text" value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]" required data-testid="product-name-input" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
          <select value={productForm.category} onChange={(e) => setProductForm({ ...productForm, category: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]" data-testid="product-category-select">
            <option value="gold">Gold</option>
            <option value="silver">Silver</option>
            <option value="diamond">Diamond</option>
            <option value="platinum">Platinum</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Metal Type</label>
          <select value={productForm.metal_type} onChange={(e) => setProductForm({ ...productForm, metal_type: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]" data-testid="product-metal-type-select">
            <option value="Gold">Gold</option>
            <option value="Silver">Silver</option>
            <option value="Diamond">Diamond</option>
            <option value="Platinum">Platinum</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Metal Carat (for price calculation)</label>
          <select value={productForm.metal_carat} onChange={(e) => setProductForm({ ...productForm, metal_carat: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]" data-testid="product-metal-carat-select">
            {metalCaratOptions.map((carat) => <option key={carat} value={carat}>{carat}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Model Category</label>
          <select value={productForm.model_category} onChange={(e) => setProductForm({ ...productForm, model_category: e.target.value, model_name: '' })} className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]" data-testid="product-model-category-select">
            {modelCategoryOptions.map((category) => <option key={category} value={category}>{category}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Model Name</label>
          <select value={productForm.model_name} onChange={(e) => setProductForm({ ...productForm, model_name: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]" data-testid="product-model-name-select">
            <option value="">Select Model Name</option>
            {modelNameOptions.map((name) => <option key={name} value={name}>{name}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Weight (grams)</label>
          <input type="number" step="0.01" value={productForm.weight} onChange={(e) => setProductForm({ ...productForm, weight: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]" required data-testid="product-weight-input" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Making Charges {productForm.making_charges_type === 'percentage' ? '(%)' : '(Fixed)'}
          </label>
          <div className="flex gap-2">
            <input type="number" step="0.01" value={productForm.making_charges_percent} onChange={(e) => setProductForm({ ...productForm, making_charges_percent: e.target.value })} className="flex-1 px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]" required data-testid="product-mc-input" />
            <select value={productForm.making_charges_type} onChange={(e) => setProductForm({ ...productForm, making_charges_type: e.target.value })} className="px-3 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c] bg-white" data-testid="product-mc-type-select">
              <option value="percentage">%</option>
              <option value="fixed">Fixed</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Discount (%)</label>
          <input type="number" step="0.01" min="0" max="100" value={productForm.discount_percent} onChange={(e) => setProductForm({ ...productForm, discount_percent: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]" placeholder="e.g., 10" data-testid="product-discount-input" />
        </div>

        {/* Dimensions */}
        <div className="md:col-span-2 border-t pt-4 mt-2">
          <h4 className="text-sm font-semibold text-[#1a3a5c] mb-3 flex items-center gap-2">
            <Ruler className="h-4 w-4" /> Product Dimensions (Optional)
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Width (mm)</label>
              <input type="number" step="0.1" min="0" value={productForm.dimension_width_mm || ''} onChange={(e) => setProductForm({ ...productForm, dimension_width_mm: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]" placeholder="e.g., 25" data-testid="dimension-width-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Height (mm)</label>
              <input type="number" step="0.1" min="0" value={productForm.dimension_height_mm || ''} onChange={(e) => setProductForm({ ...productForm, dimension_height_mm: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]" placeholder="e.g., 35" data-testid="dimension-height-input" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-1">Shown on the product page as a scale comparison for customers</p>
        </div>

        {/* Stone Section */}
        <div className="md:col-span-2 border-t pt-4 mt-2">
          <h4 className="text-sm font-semibold text-[#1a3a5c] mb-3">Stone (Optional)</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Stone Weight (grams)</label>
              <input type="number" step="0.01" value={productForm.stone_weight} onChange={(e) => setProductForm({ ...productForm, stone_weight: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]" placeholder="e.g., 0.2" data-testid="product-stone-weight-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Stone Amount</label>
              <input type="number" step="0.01" value={productForm.stone_amount} onChange={(e) => setProductForm({ ...productForm, stone_amount: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]" placeholder="e.g., 500" data-testid="product-stone-amount-input" />
            </div>
          </div>
        </div>

        {/* Diamond Section */}
        <div className="md:col-span-2 border-t pt-4 mt-2">
          <h4 className="text-sm font-semibold text-[#1a3a5c] mb-3">Diamond (Optional)</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Diamond (cents)</label>
              <input type="number" step="0.01" value={productForm.diamond_cents} onChange={(e) => setProductForm({ ...productForm, diamond_cents: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]" placeholder="e.g., 100" data-testid="product-diamond-cents-input" />
              <p className="text-xs text-gray-500 mt-1">100 cents = 1 carat</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Diamond Amount</label>
              <input type="number" step="0.01" value={productForm.diamond_amount} onChange={(e) => setProductForm({ ...productForm, diamond_amount: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]" placeholder="e.g., 10000" data-testid="product-diamond-amount-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Diamond Quality</label>
              <select value={productForm.diamond_quality} onChange={(e) => setProductForm({ ...productForm, diamond_quality: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]" data-testid="product-diamond-quality-select">
                <option value="">Select Quality</option>
                <option value="FL">FL (Flawless)</option><option value="IF">IF (Internally Flawless)</option>
                <option value="VVS1">VVS1</option><option value="VVS2">VVS2</option>
                <option value="VS1">VS1</option><option value="VS2">VS2</option>
                <option value="SI1">SI1</option><option value="SI2">SI2</option>
                <option value="I1">I1</option><option value="I2">I2</option><option value="I3">I3</option>
              </select>
            </div>
          </div>
        </div>

        {/* Precious Stone + Other Material */}
        <div className="md:col-span-2 border-t pt-4 mt-2">
          <h4 className="text-sm font-semibold text-[#1a3a5c] mb-3">Precious Stone (Optional)</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Precious Stone (cents)</label>
              <input type="number" step="0.01" value={productForm.precious_stone_cents} onChange={(e) => setProductForm({ ...productForm, precious_stone_cents: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]" placeholder="e.g., 100" data-testid="product-precious-stone-cents-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Precious Stone Amount</label>
              <input type="number" step="0.01" value={productForm.precious_stone_amount} onChange={(e) => setProductForm({ ...productForm, precious_stone_amount: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]" placeholder="e.g., 5000" data-testid="product-precious-stone-amount-input" />
            </div>
          </div>
        </div>

        <div className="md:col-span-2 border-t pt-4 mt-2">
          <h4 className="text-sm font-semibold text-[#1a3a5c] mb-3">Other Material (Optional)</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Material Type</label>
              <select value={productForm.other_material_type} onChange={(e) => setProductForm({ ...productForm, other_material_type: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]" data-testid="product-other-material-type-select">
                <option value="">None</option>
                {otherMaterialOptions.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Material Weight (g)</label>
              <input type="number" step="0.01" value={productForm.other_material_weight} onChange={(e) => setProductForm({ ...productForm, other_material_weight: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]" data-testid="product-other-material-weight-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Material Amount</label>
              <input type="number" step="0.01" value={productForm.other_material_amount} onChange={(e) => setProductForm({ ...productForm, other_material_amount: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]" data-testid="product-other-material-amount-input" />
            </div>
          </div>
        </div>

        {/* Dynamic Images */}
        <div className="md:col-span-2 border-t pt-4 mt-2">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">Product Images</label>
            <button type="button" onClick={addImageSlot} className="flex items-center gap-1 text-xs text-[#C9A967] hover:text-[#b8954d] font-medium" data-testid="add-image-slot-btn">
              <Plus className="h-3.5 w-3.5" /> Add Image
            </button>
          </div>
          <div className="space-y-4">
            {productForm.images.map((imgUrl, index) => (
              <div key={`img-slot-${index}`} className="space-y-2 bg-gray-50 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-gray-400 font-medium w-4">{index + 1}</span>
                  <div className="flex-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Link className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={imgUrl || ''}
                      onChange={(e) => {
                        const newImages = [...productForm.images];
                        newImages[index] = e.target.value;
                        setProductForm({ ...productForm, images: newImages });
                      }}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
                      placeholder={`Image URL ${index + 1}${index === 0 ? ' (Required)' : ''}`}
                      data-testid={`product-image-input-${index}`}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => fileInputRefs.current[index]?.click()}
                    disabled={uploadingIndex === index}
                    className={`px-3 py-2 rounded-sm flex items-center gap-1.5 transition-colors whitespace-nowrap ${
                      uploadingIndex === index ? 'bg-gray-300 cursor-not-allowed' : 'bg-[#C9A967] hover:bg-[#b8954d] text-white'
                    }`}
                    data-testid={`upload-image-btn-${index}`}
                  >
                    {uploadingIndex === index ? (
                      <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div><span className="text-sm">...</span></>
                    ) : (
                      <><Upload className="h-4 w-4" /><span className="text-sm">Upload</span></>
                    )}
                  </button>
                  <input ref={(el) => (fileInputRefs.current[index] = el)} type="file" accept="image/jpeg,image/jpg,image/png,image/gif,image/webp" onChange={(e) => handleImageUpload(index, e.target.files?.[0])} className="hidden" />
                  {productForm.images.length > 1 && (
                    <button type="button" onClick={() => removeImageSlot(index)} className="p-2 text-red-400 hover:text-red-600 transition-colors" title="Remove image" data-testid={`remove-image-btn-${index}`}>
                      <X className="h-4 w-4" />
                    </button>
                  )}
                  {imgUrl && (
                    <img src={imgUrl} alt={`Preview ${index + 1}`} className="h-10 w-10 object-cover rounded border border-gray-200" style={productForm.image_focal_points?.[index] ? { objectPosition: `${productForm.image_focal_points[index].x}% ${productForm.image_focal_points[index].y}%` } : undefined} onError={(e) => { e.target.style.display = 'none'; }} />
                  )}
                </div>
                {imgUrl && (
                  <FocalPointPicker
                    src={imgUrl}
                    focalX={productForm.image_focal_points?.[index]?.x ?? 50}
                    focalY={productForm.image_focal_points?.[index]?.y ?? 50}
                    onChange={({ x, y }) => {
                      const points = [...(productForm.image_focal_points || productForm.images.map(() => ({x:50,y:50})))];
                      points[index] = { x, y };
                      setProductForm({ ...productForm, image_focal_points: points });
                    }}
                  />
                )}
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">First image is the main product image. Click Upload or paste a URL.</p>
        </div>

        {/* Description */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
          <textarea value={productForm.description} onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]" rows="3" required data-testid="product-description-input" />
        </div>

        {/* Toggles */}
        <div className="md:col-span-2 space-y-2">
          <label className="flex items-center space-x-2">
            <input type="checkbox" checked={productForm.is_featured} onChange={(e) => setProductForm({ ...productForm, is_featured: e.target.checked })} className="rounded border-gray-300 text-[#1a3a5c] focus:ring-[#1a3a5c]" data-testid="product-featured-checkbox" />
            <span className="text-sm font-medium text-gray-700">Featured Product</span>
          </label>
          <label className="flex items-center space-x-2">
            <input type="checkbox" checked={productForm.is_sold_out} onChange={(e) => setProductForm({ ...productForm, is_sold_out: e.target.checked })} className="rounded border-gray-300 text-red-600 focus:ring-red-500" data-testid="product-soldout-checkbox" />
            <span className="text-sm font-medium text-gray-700">Mark as Sold Out</span>
          </label>
          {productForm.is_sold_out && (
            <label className="flex items-center space-x-2 ml-6">
              <input type="checkbox" checked={productForm.hide_when_sold_out} onChange={(e) => setProductForm({ ...productForm, hide_when_sold_out: e.target.checked })} className="rounded border-gray-300 text-orange-600 focus:ring-orange-500" data-testid="product-hide-soldout-checkbox" />
              <span className="text-sm font-medium text-gray-700">Hide from product listing</span>
            </label>
          )}
          <label className="flex items-center space-x-2">
            <input type="checkbox" checked={productForm.is_by_order} onChange={(e) => setProductForm({ ...productForm, is_by_order: e.target.checked })} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
            <span className="text-sm font-medium text-gray-700">By Order (Always shows in stock)</span>
          </label>
        </div>

        {/* Stock & Gender & Barcode */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Stock Quantity</label>
          <input type="number" value={productForm.stock_quantity} onChange={(e) => setProductForm({ ...productForm, stock_quantity: parseInt(e.target.value) || 0 })} className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]" min="0" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
          <div className="space-y-2 border border-gray-300 rounded-sm p-3">
            {['Women', 'Men', 'Kids', 'Unisex'].map((g) => (
              <label key={g} className="flex items-center space-x-2 cursor-pointer">
                <input type="checkbox" checked={(productForm.gender || []).includes(g.toLowerCase())} onChange={(e) => {
                  const current = productForm.gender || [];
                  const val = g.toLowerCase();
                  setProductForm({ ...productForm, gender: e.target.checked ? [...current, val] : current.filter(item => item !== val) });
                }} className="rounded border-gray-300 text-[#1a3a5c] focus:ring-[#1a3a5c]" data-testid={`gender-${g.toLowerCase()}`} />
                <span className="text-sm text-gray-700">{g}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Barcode Number</label>
          <input type="text" value={productForm.barcode || ''} onChange={(e) => setProductForm({ ...productForm, barcode: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]" placeholder="Enter barcode" data-testid="barcode-input" />
          <p className="text-xs text-gray-500 mt-1">Internal tracking only</p>
        </div>

        {/* Length Options */}
        <div className="md:col-span-2 border-t pt-4 mt-4">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-gray-700">Length Options</label>
            <label className="flex items-center space-x-2">
              <input type="checkbox" checked={productForm.length_enabled} onChange={(e) => setProductForm({ ...productForm, length_enabled: e.target.checked })} className="rounded border-gray-300 text-[#1a3a5c] focus:ring-[#1a3a5c]" />
              <span className="text-sm text-gray-600">Enable</span>
            </label>
          </div>
          {productForm.length_enabled && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                {productForm.length_options.map((opt, idx) => (
                  <span key={`len-${opt}`} className="inline-flex items-center px-3 py-1 bg-gray-100 rounded-full text-sm">{opt}<button type="button" onClick={() => setProductForm({ ...productForm, length_options: productForm.length_options.filter((_, i) => i !== idx) })} className="ml-2 text-gray-500 hover:text-red-500"><X className="h-3 w-3" /></button></span>
                ))}
              </div>
              <div className="flex gap-2">
                <select value={newLengthOption} onChange={(e) => setNewLengthOption(e.target.value)} className="flex-1 px-3 py-2 border border-gray-300 rounded-sm text-sm">
                  <option value="">Select length...</option>
                  {defaultLengthOptions.filter(o => !productForm.length_options.includes(o)).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
                <button type="button" onClick={() => { if (newLengthOption && !productForm.length_options.includes(newLengthOption)) { setProductForm({ ...productForm, length_options: [...productForm.length_options, newLengthOption] }); setNewLengthOption(''); } }} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-sm hover:bg-gray-300 text-sm">Add</button>
              </div>
            </div>
          )}
        </div>

        {/* Color Options */}
        <div className="md:col-span-2 border-t pt-4">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-gray-700">Color Options</label>
            <label className="flex items-center space-x-2">
              <input type="checkbox" checked={productForm.color_enabled} onChange={(e) => setProductForm({ ...productForm, color_enabled: e.target.checked })} className="rounded border-gray-300 text-[#1a3a5c] focus:ring-[#1a3a5c]" />
              <span className="text-sm text-gray-600">Enable</span>
            </label>
          </div>
          {productForm.color_enabled && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                {productForm.color_options.map((opt, idx) => (
                  <span key={`clr-${opt}`} className="inline-flex items-center px-3 py-1 bg-gray-100 rounded-full text-sm">{opt}<button type="button" onClick={() => setProductForm({ ...productForm, color_options: productForm.color_options.filter((_, i) => i !== idx) })} className="ml-2 text-gray-500 hover:text-red-500"><X className="h-3 w-3" /></button></span>
                ))}
              </div>
              <div className="flex gap-2">
                <select value={newColorOption} onChange={(e) => setNewColorOption(e.target.value)} className="flex-1 px-3 py-2 border border-gray-300 rounded-sm text-sm">
                  <option value="">Select color...</option>
                  {defaultColorOptions.filter(o => !productForm.color_options.includes(o)).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
                <button type="button" onClick={() => { if (newColorOption && !productForm.color_options.includes(newColorOption)) { setProductForm({ ...productForm, color_options: [...productForm.color_options, newColorOption] }); setNewColorOption(''); } }} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-sm hover:bg-gray-300 text-sm">Add</button>
              </div>
            </div>
          )}
        </div>

        {/* Submit */}
        <div className="md:col-span-2 flex justify-end space-x-4 border-t pt-4 mt-4">
          <button type="button" onClick={onCancel} className="px-6 py-2 border border-gray-300 text-gray-700 rounded-sm hover:bg-gray-50 transition-colors">Cancel</button>
          <button type="submit" className="bg-[#1a3a5c] text-white px-6 py-2 rounded-sm hover:bg-[#1f4268] transition-colors flex items-center space-x-2" data-testid="save-product-button">
            <Save className="h-4 w-4" />
            <span>{editingProduct ? 'Update' : 'Save'}</span>
          </button>
        </div>
      </form>
    </motion.div>
  );
}

export { MODEL_NAMES };
